import {PropertyValuation, SimpleSelectOption} from "../../../../api/ApiTypes.ts";
import React from "react";
import {isCompTypeActive} from "../../dyanimcFormUtil.tsx";
import {Collision, CollisionDetection} from "@dnd-kit/core";
import {CompsDataGridInfo} from "../../DynamicFormTypes.ts";
import {SortingMethod, SortingOption} from "./CompsDragDropTable.tsx";
import {Home} from "lucide-react";
import {prettyFormatUnknownEnum} from "../../../../utils/kebabhelper.ts";


export interface SearchOptions {
    yearBuiltOptions: SimpleSelectOption[];
    grossLivingOptions: SimpleSelectOption[];
    lotSizeOptions: SimpleSelectOption[];
    bedroomOptions: SimpleSelectOption[];
    bathroomOptions: SimpleSelectOption[];
    propertyTypeOptions: SimpleSelectOption[];
    dateRangeOptions: SimpleSelectOption[];
    searchTypeOptions: SimpleSelectOption[];
    suburbanDistanceOptions: SimpleSelectOption[];
    urbanDistanceOptions: SimpleSelectOption[];
    ruralDistanceOptions: SimpleSelectOption[];
    searchTargetOptions: SimpleSelectOption[];
}

export const COMPS_SEARCH_OPTIONS: SearchOptions = {
    yearBuiltOptions: [
        {label: "Any", value: ""},
        {label: "Within 10 Years of Subject", value: "10"},
        {label: "Within 20 Years of Subject", value: "20"},
        {label: "Within 30 Years of Subject", value: "30"},
        {label: "Within 50 Years of Subject", value: "50"}
    ],
    grossLivingOptions: [
        {label: "Any", value: ""},
        {label: "Within 10% of Subject", value: "0.1"},
        {label: "Within 20% of Subject", value: "0.2"},
        {label: "Within 30% of Subject", value: "0.3"},
        {label: "Within 50% of Subject", value: "0.5"}
    ],
    lotSizeOptions: [
        {label: "Any", value: ""},
        {label: "Within 10% of Subject", value: "0.1"},
        {label: "Within 20% of Subject", value: "0.2"},
        {label: "Within 30% of Subject", value: "0.3"},
        {label: "Within 50% of Subject", value: "0.5"}
    ],
    bedroomOptions: [
        {label: "Any", value: ""},
        {label: "Equal to Subject", value: "0"},
        {label: "+/- 1 compared to Subject", value: "1"},
        {label: "+/- 2 compared to Subject", value: "2"},
        {label: "+/- 3 compared to Subject", value: "3"}
    ],
    bathroomOptions: [
        {label: "Any", value: ""},
        {label: "Equal to Subject", value: "0"},
        {label: "+/- 1 compared to Subject", value: "1"},
        {label: "+/- 2 compared to Subject", value: "2"},
        {label: "+/- 3 compared to Subject", value: "3"}
    ],
    propertyTypeOptions: [
        {label: "Best Match", value: "best-match"},
        {label: "Single Family", value: "RSFR"},
        {label: "Condo", value: "RCON"},
        {label: "Multi-Family", value: "RDUP"},
        {label: "Mobile Home", value: "RMOB"},
        {label: "Miscellaneous", value: "RMSC"},
        {label: "Vacant Residential Land", value: "VRES"},
    ],
    dateRangeOptions: [
        {label: "Last 6 Months", value: "6"},
        {label: "Last 9 Months", value: "9"},
        {label: "Last 12 Months", value: "12"},
        {label: "Last 18 Months", value: "18"}
    ],
    searchTypeOptions: [
        {label: "Distance In Zip Code", value: "distance"},
        {label: "Distance", value: "distancenozip"},
    ],
    suburbanDistanceOptions: [
        {label: "1 Mile from Subject", value: "1"},
        {label: "2 Miles from Subject", value: "2"},
        {label: "3 Miles from Subject", value: "3"},
        {label: "5 Miles from Subject", value: "5"},
        {label: "10 Miles from Subject", value: "10"}
    ],
    urbanDistanceOptions: [
        {label: "1/4 Mile from Subject", value: "0.25"},
        {label: "1/2 Miles from Subject", value: "0.5"},
        {label: "1 Mile from Subject", value: "1"},
        {label: "2 Miles from Subject", value: "2"},
        {label: "3 Miles from Subject", value: "3"}
    ],
    ruralDistanceOptions: [
        {label: "5 Miles from Subject", value: "5"},
        {label: "10 Miles from Subject", value: "10"},
        {label: "25 Mile from Subject", value: "25"},
        {label: "50 Miles from Subject", value: "50"}
    ],
    searchTargetOptions: [
        {label: "Top 10+ matches", value: "10"},
        {label: "Top 25+ matches", value: "25"},
        {label: "Top 50+ matches", value: "50"},
        {label: "All Matches", value: ""},
    ]
}

export interface CompChangedResponse {
    ok: boolean,
    message: string,
}



// Column definitions - single source of truth for table structure
// Each column can have a string header or a React node for multi-line headers
export interface ColumnConfig {
    key: string;
    header: ((handleSortClick: (field: SortingOption) => void, currentSortingMethod: SortingMethod) => React.ReactNode);
    className?: string;
    align?: 'left' | 'center' | 'right';
    priority?: 'high' | 'medium' | 'low'; // Add priority for responsive hiding
    minWidth?: string; // Minimum width for the column
    render: (comp: PropertyValuation | undefined, alreadySelected: boolean) => React.ReactNode;
}

// Enhanced column definitions with responsive properties
export const COLUMN_CONFIGS: ColumnConfig[] = [
    {
        key: 'index',
        header: (handleSortClick, currentSortingMethod) => (
            <div className="text-center">
                <span
                    className="cursor-pointer block font-medium text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.RANKING);
                    }}
                >
                    ID
                    {currentSortingMethod.label === SortingOption.RANKING && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
            </div>
        ),        align: 'center',
        priority: 'high',
        minWidth: 'min-w-[50px]',
        render: (comp, alreadySelected) => {
            return (
                <div className="flex items-center justify-center">
                    <div
                        className={`w-7 h-7 rounded-full ${isCompTypeActive(comp?.compType) ? "bg-green-500" : "bg-blue-600"} ${alreadySelected ? "border-2 border-black" : ""} text-white flex items-center justify-center text-xs font-semibold shadow-sm`}>
                        {comp?.ranking || <Home className="w-4 h-4"/>}
                    </div>
                </div>
            )
        }
    },
    {
        key: 'mlsId',
        header: (handleSortClick, currentSortingMethod) => (
            <div className="text-center">
                <span
                    className="cursor-pointer block font-medium text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.MLSID);
                    }}
                >
                    MLS
                    {currentSortingMethod.label === SortingOption.MLSID && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
            </div>
        ),
        priority: 'medium',
        minWidth: 'min-w-[70px]',
        className: 'hidden lg:table-cell', // Hide on smaller screens
        render: (comp) => (
            <div className="font-medium text-blue-800 text-sm">{comp?.mlsId || '-'}</div>
        )
    },
    {
        key: 'address',
        header: (handleSortClick, currentSortingMethod) => (
            <div className="text-center">
                <span
                    className="cursor-pointer block font-medium text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.ADDRESS);
                    }}
                >
                    Address
                    {currentSortingMethod.label === SortingOption.ADDRESS && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
            </div>
        ),
        priority: 'high',
        minWidth: 'min-w-[120px]',
        render: (comp) => (
            <div className="text-left">
                <div className="max-w-[150px] xl:max-w-xs truncate text-sm" title={comp?.address}>
                    {comp?.address || '-'}
                </div>
                {/* Show MLS on mobile when MLS column is hidden */}
                <div className="lg:hidden text-xs text-gray-500 mt-0.5">
                    MLS: {comp?.mlsId || '-'}
                </div>
            </div>
        )
    },
    {
        key: 'proximity-prop-type',
        header: (handleSortClick, currentSortingMethod) => (
            <div className="text-center">
                <span
                    className="cursor-pointer block font-medium text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.PROXIMITY);
                    }}
                >
                    Proximity
                    {currentSortingMethod.label === SortingOption.PROXIMITY && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
                <span
                    className="cursor-pointer block text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.PROPERTY_TYPE);
                    }}
                >
                    Type
                    {currentSortingMethod.label === SortingOption.PROPERTY_TYPE && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
            </div>
        ),
        align: 'center',
        priority: 'low',
        minWidth: 'min-w-[80px]',
        className: 'hidden xl:table-cell', // Only show on larger screens
        render: (comp) => (
            <div className="text-center">
                <span
                    className="block text-sm font-medium text-gray-800">{comp?.proximityInMiles || '-'} mi</span>
                <span className="block text-xs text-gray-600">{comp?.propertyType || '-'}</span>
            </div>
        )
    },
    {
        key: 'active-sold-sale_type',
        header: (handleSortClick, currentSortingMethod) => (
            <div className="text-center">
                <span
                    className="cursor-pointer block font-medium text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.SALE_STATUS);
                    }}
                >
                    Status
                    {currentSortingMethod.label === SortingOption.SALE_STATUS && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
                <span
                    className="cursor-pointer block text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.SALE_TYPE);
                    }}
                >
                    Type
                    {currentSortingMethod.label === SortingOption.SALE_TYPE && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
            </div>
        ),
        align: 'center',
        priority: 'medium',
        minWidth: 'min-w-[80px]',
        render: (comp) => {
            const statusColors = isCompTypeActive(comp?.compType)
                ? "bg-green-100 text-green-800 border-green-200"
                : "bg-blue-100 text-blue-800 border-blue-200";

            return (
                <div className="text-center space-y-1">
                    <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColors} border`}>
                        {prettyFormatUnknownEnum(comp?.saleStatus) ?? "-"}
                    </span>
                    <span className="hidden md:block text-xs text-gray-600">
                        {prettyFormatUnknownEnum(comp?.saleType) ?? "-"}
                    </span>
                </div>
            );
        }
    },
    {
        key: 'date-price',
        header: (handleSortClick, currentSortingMethod) => (
            <div className="text-center">
                <span
                    className="cursor-pointer block font-medium text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.LAST_TRANSACTION_DATE);
                    }}
                >
                    Date
                    {currentSortingMethod.label === SortingOption.LAST_TRANSACTION_DATE && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
                <span
                    className="cursor-pointer block text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.LAST_TRANSACTION_PRICE);
                    }}
                >
                    Price
                    {currentSortingMethod.label === SortingOption.LAST_TRANSACTION_PRICE && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
            </div>
        ),
        align: 'center',
        priority: 'high',
        minWidth: 'min-w-[90px]',
        render: (comp) => (
            <div className="text-center">
                <span
                    className="block text-xs text-gray-800">{comp?.lastTransactionDate || '-'}</span>
                <span className="block text-sm font-medium text-blue-800">
                    ${comp?.lastTransactionPrice ? new Intl.NumberFormat('en-US', {
                    notation: 'compact',
                    maximumFractionDigits: 4
                }).format(Number(comp?.lastTransactionPrice)) : "-"}
                </span>
            </div>
        )
    },
    {
        key: 'orig_price-dom',
        header: (handleSortClick, currentSortingMethod) => (
            <div className="text-center">
                <span
                    className="cursor-pointer block font-medium text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.ORIGINAL_PRICE);
                    }}
                >
                    Orig
                    {currentSortingMethod.label === SortingOption.ORIGINAL_PRICE && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
                <span
                    className="cursor-pointer block text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.DAYS_ON_MARKET);
                    }}
                >
                    DOM
                    {currentSortingMethod.label === SortingOption.DAYS_ON_MARKET && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
            </div>
        ),
        align: 'center',
        priority: 'low',
        minWidth: 'min-w-[70px]',
        className: 'hidden lg:table-cell',
        render: (comp) => (
            <div className="text-center">
                <span className="block text-xs text-gray-800">
                    ${comp?.originalPrice ? new Intl.NumberFormat('en-US', {
                    notation: 'compact',
                    maximumFractionDigits: 4
                }).format(comp?.originalPrice) : "-"}
                </span>
                <span className="inline-flex items-center text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-800">
                    {comp?.daysOnMarket || 0}d
                </span>
            </div>
        )
    },
    {
        key: 'all-bed-bath-stories-unit',
        header: (handleSortClick, currentSortingMethod) => (
            <>
                <div className="flex justify-center items-center gap-0.5 mb-0.5">
                    <span
                        className="cursor-pointer block font-medium text-xs hover:text-blue-600 hover:underline"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSortClick(SortingOption.ALL_ROOMS_COUNT);
                        }}
                    >
                        All
                        {currentSortingMethod.label === SortingOption.ALL_ROOMS_COUNT && (
                            <span className="ml-1 text-blue-600">
                                {currentSortingMethod.descending ? "↓" : "↑"}
                            </span>
                        )}
                    </span>
                        |
                        <span
                            className="cursor-pointer block font-medium text-xs hover:text-blue-600 hover:underline"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSortClick(SortingOption.BEDROOM_COUNT);
                            }}
                        >
                        Bed
                            {currentSortingMethod.label === SortingOption.BEDROOM_COUNT && (
                                <span className="ml-1 text-blue-600">
                                {currentSortingMethod.descending ? "↓" : "↑"}
                            </span>
                            )}
                    </span>
                        |
                        <span
                            className="cursor-pointer block font-medium text-xs hover:text-blue-600 hover:underline"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSortClick(SortingOption.FULL_BATHROOM_COUNT);
                            }}
                        >
                        Bath
                            {currentSortingMethod.label === SortingOption.FULL_BATHROOM_COUNT && (
                                <span className="ml-1 text-blue-600">
                                {currentSortingMethod.descending ? "↓" : "↑"}
                            </span>
                            )}
                    </span>
                </div>
                <div className="flex justify-center items-center gap-0.5 mb-0.5">
                    <span
                        className="cursor-pointer block text-xs hover:text-blue-600 hover:underline"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSortClick(SortingOption.STORIES);
                        }}
                    >
                        Stories
                        {currentSortingMethod.label === SortingOption.STORIES && (
                            <span className="ml-1 text-blue-600">
                                {currentSortingMethod.descending ? "↓" : "↑"}
                            </span>
                        )}
                    </span>
                        |
                    <span
                        className="cursor-pointer block font-medium text-xs hover:text-blue-600 hover:underline"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSortClick(SortingOption.UNITS);
                        }}
                    >
                        Units
                        {currentSortingMethod.label === SortingOption.UNITS && (
                            <span className="ml-1 text-blue-600">
                                {currentSortingMethod.descending ? "↓" : "↑"}
                            </span>
                            )}
                    </span>
                </div>
            </>
        ),
        align: 'center',
        priority: 'high',
        minWidth: 'min-w-[100px]',
        render: (comp) => (
            <div className="text-center">
                <div className="flex justify-center items-center gap-0.5 mb-0.5">
                    <span className="px-1.5 py-0.5 bg-blue-50 rounded text-xs text-blue-800 font-medium">
                        {comp?.allRoomsCount ?? '—'}
                    </span>
                    <span className="text-xs text-gray-500">|</span>
                    <span className="px-1.5 py-0.5 bg-blue-50 rounded text-xs text-blue-800 font-medium">
                        {comp?.bedroomCount ?? '—'}
                    </span>
                    <span className="text-xs text-gray-500">|</span>
                    <span className="px-1.5 py-0.5 bg-blue-50 rounded text-xs text-blue-800 font-medium">
                        {comp?.fullBathroomCount ?? '—'}
                    </span>
                </div>
                <div className="text-xs text-gray-600 hidden md:block">
                    {comp?.stories ?? '—'}st | {comp?.units ?? '—'}u
                </div>
            </div>
        )
    },
    {
        key: 'gla-year_built',
        header: (handleSortClick, currentSortingMethod) => (
            <div className="text-center">
                <span
                    className="cursor-pointer block font-medium text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.GROSS_LIVING_AREA);
                    }}
                >
                    GLA
                    {currentSortingMethod.label === SortingOption.GROSS_LIVING_AREA && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
                <span
                    className="cursor-pointer block text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.YEAR_BUILT);
                    }}
                >
                    Year
                    {currentSortingMethod.label === SortingOption.YEAR_BUILT && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
            </div>
        ),
        align: 'center',
        priority: 'medium',
        minWidth: 'min-w-[80px]',
        render: (comp) => (
            <div className="text-center">
                <span className="block text-sm font-medium">
                    {comp?.grossLivingArea ? `${new Intl.NumberFormat('en-US').format(comp?.grossLivingArea)} sqft` : '—'}
                </span>
                <span className="block text-xs text-gray-600">
                    {comp?.yearBuilt || '—'}
                </span>
            </div>
        )
    },
    {
        key: 'lot_size-garage',
        header: (handleSortClick, currentSortingMethod) => (
            <div className="text-center">
                <span
                    className="cursor-pointer block font-medium text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.LOT_SIZE);
                    }}
                >
                    Lot
                    {currentSortingMethod.label === SortingOption.LOT_SIZE && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
                <span
                    className="cursor-pointer block text-xs hover:text-blue-600 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSortClick(SortingOption.GARAGE);
                    }}
                >
                    Garage
                    {currentSortingMethod.label === SortingOption.GARAGE && (
                        <span className="ml-1 text-blue-600">
                            {currentSortingMethod.descending ? "↓" : "↑"}
                        </span>
                    )}
                </span>
            </div>
        ),
        align: 'center',
        priority: 'low',
        minWidth: 'min-w-[70px]',
        className: 'hidden xl:table-cell',
        render: (comp) => (
            <div className="text-center">
                <span className="block text-xs">
                    {comp?.lotSize ? `${new Intl.NumberFormat('en-US').format(comp?.lotSize)} sqft` : '—'}
                </span>
                <span className="block text-xs text-gray-600">
                    {comp?.garage || '—'}
                </span>
            </div>
        )
    }
];

export const getAlignmentClass = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
        case 'center': return 'text-center';
        case 'right': return 'text-right';
        default: return 'text-left';
    }
};
export const customCollisionDetection: CollisionDetection = (args): Collision[] => {
    const { droppableRects, droppableContainers, pointerCoordinates } = args;
    const collisions: Collision[] = [];

    if (!pointerCoordinates) {
        return collisions;
    }

    for (const container of droppableContainers) {
        const rect = droppableRects.get(container.id);
        if (rect &&
            pointerCoordinates.x >= rect.left &&
            pointerCoordinates.x <= rect.right &&
            pointerCoordinates.y >= rect.top &&
            pointerCoordinates.y <= rect.bottom) {
            collisions.push({
                id: container.id,
                data: { droppableContainer: container, value: 0 }
            });
        }
    }

    return collisions;
}

export interface SelectableComp {
    propertyValuation?: PropertyValuation,
    dgInfo: CompsDataGridInfo;
}

export interface SelectedComp {
    dgInfo: CompsDataGridInfo;
    indexOfDg: number;
    formValues?: Record<string, any>;
    address?: string;
    mlsId?: string;
}



export interface SlotOption {
    label: string,
    selectedComp: SelectedComp
}


